import { action } from '@reatom/framework'
import { match, P } from 'ts-pattern'

import { Result } from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { WrongCredentialsError } from '@root/features/auth'
import { WrongPasswordError } from '@root/features/secure-storage/secure-storage-error'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { Error } from '@root/shared'
import { reatomRequest } from '@root/shared/lib'

import {
  getTransferInstructionsRequestAction,
  sendTransferInstructionsRequestAction,
} from './transfer-requests'

import type { Redacted, TypeHelper } from '@blade/std-lib'
import type {
  WalletGetTransferInstructionsCreatePayload as WalletGetTransferInstructionsCreatePayloadDto,
  WalletSendTransferTransactionCreateData,
} from '@blade/trade-api-client'
import type { CurrencyModel } from '@root/entities/currency'
import type { TradeSettings } from '@root/entities/trade-settings'
import type { WalletId } from '@root/entities/wallet'
import type BigNumber from 'bignumber.js'

type TransferArgs = {
  amount: BigNumber
  currency: CurrencyModel
  address: string
  maxBaseFeeGwei: number
  priorityFeeGwei: number
  svm: TradeSettings.SvmSettings
  password: Redacted.Redacted
  walletId: WalletId
}

const processTransferAction = action(
  async (
    ctx,
    args: TransferArgs,
  ): Promise<
    Result.Result<
      WalletSendTransferTransactionCreateData,
      Error.UnknownError | WrongCredentialsError
    >
  > => {
    const network = ctx.get(selectedNetworkAtom)
    const { secureStorageService } = ctx.get(dependenciesAtom)

    type SettingsDto = WalletGetTransferInstructionsCreatePayload['settings']
    const settingsDto = match(network)
      .returnType<SettingsDto>()
      .with(Network.Ton, (): SettingsDto => ({}))
      .with(
        Network.Solana,
        (): SettingsDto => ({
          jito: args.svm.jitoEnabled,
          jitoTip: args.svm.jitoTip,
          priorityFee: args.svm.priorityFee.toString(),
        }),
      )
      .with(
        P.union(Network.Ethereum, Network.Bsc, Network.Base),
        (): SettingsDto => ({
          gasPrice: undefined,
          maxGasPrice: args.maxBaseFeeGwei,
          maxPriorityFeePerGas: args.priorityFeeGwei,
        }),
      )
      .exhaustive()

    interface WalletGetTransferInstructionsCreatePayload
      extends TypeHelper.OmitKey<
        WalletGetTransferInstructionsCreatePayloadDto,
        'walletId'
      > {
      walletId: WalletId
    }

    const req: WalletGetTransferInstructionsCreatePayload = {
      amount: args.amount.toNumber(),
      destination: args.address,
      network: Network.toDto(network),
      settings: settingsDto,
      token: args.currency.address,
      walletId: args.walletId,
    }

    const instructionsResult = await ctx.schedule(() =>
      getTransferInstructionsRequestAction(ctx, req),
    )

    if (Result.isFailure(instructionsResult)) {
      return Result.fail(instructionsResult.failure)
    }

    const signedTxResult = await ctx.schedule(() =>
      secureStorageService.signTransaction({
        network,
        password: args.password,
        walletId: req.walletId,
        tx: instructionsResult.success.tx,
        operation: 'transfer',
      }),
    )

    if (Result.isFailure(signedTxResult)) {
      if (signedTxResult.failure instanceof WrongPasswordError) {
        return Result.fail(new WrongCredentialsError())
      }
      return Result.fail(Error.UnknownError.fromUnknown(signedTxResult.failure))
    }

    const sendResult = await ctx.schedule(() =>
      sendTransferInstructionsRequestAction(ctx, {
        ...req,
        signedTx: signedTxResult.success.txSigned,
      }),
    )

    return sendResult
  },
  'processTransferAction',
)

export const reatomTransfer = () => {
  const transferStateAtom = reatomRequest({
    execAction: processTransferAction,
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'reatomTransfer',
  })

  return transferStateAtom
}
